import { z } from 'zod';
import { Uuid } from './blocks.js';
import { ContentBuildJobId } from './build-protocol.js';
import { ContentReleaseV1 } from './releases.js';
export const AdminRole = z.enum(['Owner', 'Editor']);
export const ReleaseKind = z.enum(['Preview', 'Production']);
export const PreviewBuildStatus = z.enum(['Queued', 'Building', 'Ready', 'Failed', 'TimedOut']);
export const CreatePreviewReleaseRequest = z
    .object({ sourceRevisions: z.record(z.string(), z.number().int().nonnegative()) })
    .strict();
export const PreviewReleaseResponse = z
    .object({ releaseId: Uuid, kind: z.literal('Preview') })
    .strict();
export const PreviewSourceRevisionsResponse = z
    .object({
    sourceRevisions: z.record(z.string().min(1), z.number().int().nonnegative()),
})
    .strict();
export const PendingChangeKind = z.enum([
    'Settings',
    'Page',
    'Pricing',
    'Gallery',
    'Journal',
    'Media',
    'Removed',
]);
export const PendingChange = z
    .object({
    key: z.string().min(1),
    kind: PendingChangeKind,
    label: z.string().min(1),
    route: z.string().startsWith('/').nullable(),
    siteAccess: z.enum(['Public', 'AdminOnly']).nullable(),
    details: z.array(z.string().min(1)).min(1),
    affectsWebsite: z.boolean(),
    changedAt: z.iso.datetime().nullable(),
})
    .strict();
export const PendingChangesResponse = z
    .object({
    count: z.number().int().nonnegative(),
    websiteCount: z.number().int().nonnegative(),
    adminOnlyCount: z.number().int().nonnegative(),
    changes: z.array(PendingChange),
    latestProductionReleaseId: Uuid.nullable(),
    lastPublishedAt: z.iso.datetime().nullable(),
    sourceRevisions: z.record(z.string().min(1), z.number().int().nonnegative()),
})
    .strict();
export const CreateProductionReleaseRequest = z
    .object({
    sourceRevisions: z.record(z.string().min(1), z.number().int().nonnegative()),
})
    .strict();
export const ProductionReleaseResponse = z
    .object({ releaseId: Uuid, kind: z.literal('Production') })
    .strict();
const ReleaseSummary = z
    .object({
    id: Uuid,
    kind: ReleaseKind,
    status: PreviewBuildStatus,
    latestAttemptId: Uuid,
    readyAttemptId: Uuid.nullable(),
    diagnosticSummary: z.string().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
})
    .strict();
export const PreviewReleaseSummary = ReleaseSummary.extend({ kind: z.literal('Preview') }).strict();
export const ProductionReleaseSummary = ReleaseSummary.extend({
    kind: z.literal('Production'),
}).strict();
export const AnyReleaseSummary = z.union([PreviewReleaseSummary, ProductionReleaseSummary]);
export const ReleaseBuildAttempt = z
    .object({
    id: Uuid,
    releaseId: Uuid,
    attemptNumber: z.number().int().positive(),
    buildJobId: z.string().min(1),
    status: PreviewBuildStatus,
    githubRunId: z.string().nullable(),
    githubAttemptId: z.string().nullable(),
    artifactHash: z.string().nullable(),
    artifactPrefix: z.string().nullable(),
    commitSha: z
        .string()
        .regex(/^[0-9a-f]{40}$/)
        .nullable(),
    errorSummary: z.string().nullable(),
    startedAt: z.iso.datetime().nullable(),
    finishedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
})
    .strict();
export const PreviewReleaseDetailResponse = z
    .object({ release: PreviewReleaseSummary.extend({ snapshot: ContentReleaseV1 }).strict() })
    .strict();
export const ProductionReleaseDetailResponse = z
    .object({ release: ProductionReleaseSummary.extend({ snapshot: ContentReleaseV1 }).strict() })
    .strict();
export const AnyReleaseDetailResponse = z.union([
    PreviewReleaseDetailResponse,
    ProductionReleaseDetailResponse,
]);
export const ReleaseBuildAttemptsResponse = z
    .object({ attempts: z.array(ReleaseBuildAttempt) })
    .strict();
export const ReleaseBuildAttemptResponse = z.object({ attempt: ReleaseBuildAttempt }).strict();
export const BuildTokenRequest = z
    .object({ releaseId: Uuid, buildJobId: ContentBuildJobId })
    .strict();
export const BuildTokenResponse = z
    .object({ token: z.string().min(1), expiresInSeconds: z.number().int().positive() })
    .strict();
export const BuildAssetPin = z.discriminatedUnion('sourceType', [
    z
        .object({
        assetId: Uuid,
        sourceType: z.enum(['R2', 'External']),
        checksum: z.string().regex(/^[0-9a-f]{64}$/),
        downloadUrl: z.url(),
    })
        .strict(),
    z
        .object({
        assetId: Uuid,
        sourceType: z.literal('Bundled'),
        checksum: z.string().regex(/^[0-9a-f]{64}$/),
        publicPath: z.string().regex(/^\/images\/[A-Za-z0-9._/-]+$/),
    })
        .strict(),
]);
export const BuildReleaseResponse = z
    .object({ release: ContentReleaseV1, assets: z.array(BuildAssetPin) })
    .strict();
export const BuildStartCallbackRequest = z
    .object({
    release_id: Uuid,
    build_job_id: ContentBuildJobId,
    run_id: z.string().trim().min(1),
    run_attempt: z.number().int().positive(),
})
    .strict();
export const BuildTerminalCallbackRequest = BuildStartCallbackRequest.extend({
    result: z.enum(['ready', 'failed', 'timed_out']),
    artifact_hash: z
        .string()
        .regex(/^[0-9a-f]{64}$/)
        .nullable(),
    artifact_prefix: z.string().min(1).nullable(),
    commit_sha: z
        .string()
        .regex(/^[0-9a-f]{40}$/)
        .nullable(),
    error_summary: z.string().max(500).nullable(),
}).strict();
export const BuildCallbackResponse = z
    .object({
    outcome: z.enum(['applied', 'idempotent', 'mismatch', 'stale', 'out_of_order']),
})
    .strict();
const ErrorBody = (code, message) => z
    .object({
    error: z.object({ code, message }).strict(),
})
    .strict();
export const NotFoundErrorResponse = ErrorBody(z.literal('NOT_FOUND'), z.string().min(1));
export const ValidationErrorResponse = z
    .object({
    error: z
        .object({
        code: z.literal('VALIDATION_FAILED'),
        message: z.literal('Request validation failed'),
        fields: z.array(z.object({ path: z.string(), message: z.string().min(1) }).strict()),
    })
        .strict(),
})
    .strict();
export const UnauthorizedErrorResponse = ErrorBody(z.literal('UNAUTHORIZED'), z.literal('Authentication required'));
export const ForbiddenErrorResponse = ErrorBody(z.literal('FORBIDDEN'), z.string().min(1));
export const CsrfRejectedErrorResponse = ErrorBody(z.literal('CSRF_REJECTED'), z.literal('Request origin or CSRF token is invalid'));
export const InternalErrorResponse = ErrorBody(z.literal('INTERNAL_ERROR'), z.literal('Request failed'));
export const ServiceUnavailableErrorResponse = ErrorBody(z.literal('SERVICE_UNAVAILABLE'), z.string().min(1));
export const RateLimitedErrorResponse = ErrorBody(z.literal('RATE_LIMITED'), z.literal('Too many requests'));
export const AdminUserConflictErrorResponse = ErrorBody(z.enum(['FINAL_OWNER', 'EMAIL_EXISTS']), z.string().min(1));
export const ReleaseConflictErrorResponse = ErrorBody(z.enum(['RELEASE_CONFLICT', 'RETRY_NOT_ALLOWED']), z.string().min(1));
export const AssetErrorResponse = z
    .object({
    error: z
        .object({
        code: z.enum([
            'NOT_FOUND',
            'UPLOAD_CONFLICT',
            'ASSET_REVISION_CONFLICT',
            'OBJECT_EXISTS',
            'ASSET_IN_USE',
            'ASSET_NOT_ASSIGNABLE',
            'VALIDATION_FAILED',
            'PAYLOAD_TOO_LARGE',
            'RATE_LIMITED',
            'SERVICE_UNAVAILABLE',
        ]),
        message: z.string().min(1),
        fields: z
            .array(z.object({ path: z.string(), message: z.string().min(1) }).strict())
            .optional(),
    })
        .strict(),
})
    .strict();
